import "keen-slider/keen-slider.min.css";
import React from "react";
import { CasestudyCard, casestudyCards } from ".";
import NewLayout from "@components/new-ui/Layout";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const SedarGlobal: React.FC = () => {
    return (
        <NewLayout
            title="Sedar Global | WhatsApp Automation in Home Furnishings"
            description="Explore how Sedar Global shifted from manual tracking to automation, boosting lead nurturing, productivity, and team collaboration with Gallabox."
            canonical="https://gallabox.com/case-study/sedar-global"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/sedar-global-og.png" }}
        >
            <CaseStudyHerosection
                title="How Sedar Global improved organisational communication with Gallabox"
                description="Explore how Sedar Global shifted from manual tracking to automation, boosting lead nurturing, productivity, and team collaboration with Gallabox."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/sedar-global.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "30%", label: "Overall improvement in business growth" },
                    { percentage: "25%", label: "Increase in employee productivity" },
                    { percentage: "15%", label: "Increase in customer satisfaction" },
                ]}
            />
            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Sedar Global"] },
                                { label: "Industry", value: ["Services"] },
                            ]}
                            product={{
                                title: "Features used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared Team Inbox" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12">
                        <section id="About">
                            <p>
                                Sedar Global is a leading brand in home furnishing and interior solutions, serving customers across the Middle East and beyond. With a large and
                                diverse customer base, Sedar needed a reliable system to organise interactions, ensure timely follow-ups, and avoid missed opportunities.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} descriptions={["Sedar faced three key challenges:"]} className="mt-0" />
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Poor organisation of conversations: With multiple leads and customers across channels, it was difficult to maintain structured communication.",
                                    },
                                    { listItem: "Inconsistent follow-ups: Without reminders or automation, many potential customers dropped off after the first interaction." },
                                    {
                                        listItem:
                                            "Limited visibility into customer status: Tracking ongoing conversations was inefficient, leading to delays and lost opportunities.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <CaseStudyInformation
                                subTitle={"Initial solutions tried"}
                                descriptions={[
                                    "Before Gallabox, Sedar attempted to manage communication through WhatsApp's native features combined with Excel spreadsheets for tracking. This system was time-consuming, error-prone, and unfit for scaling with the business.",
                                ]}
                                className="mt-12"
                            />
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"Why Sedar chose Gallabox"}
                                descriptions={[
                                    "Sedar chose Gallabox because it struck the right balance between price, usability, and customer support. The platform was not only cost-effective but also convenient to implement. Gallabox's onboarding and support team provided the guidance needed to transition smoothly.",
                                    "Sedar also evaluated Zoho SalesIQ, but it lacked the affordability, convenience, and support that Gallabox offered.",
                                    "Sedar adopted Gallabox with the following objectives:",
                                ]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Nurture leads more effectively by keeping communication timely and consistent." },
                                    { listItem: "Streamline customer communication by consolidating channels into a single platform." },
                                    { listItem: "Improve team productivity by automating routine tasks." },
                                    { listItem: "Automate customer support and follow-ups to reduce drop-offs." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"Impact on business growth"}
                                descriptions={["Since implementing Gallabox, Sedar has experienced steady improvements:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "A 30% overall improvement in business growth." },
                                    { listItem: "25% increase in employee productivity, freeing the team from repetitive tasks." },
                                    { listItem: "15% growth in customer satisfaction rates, thanks to timely follow-ups." },
                                    { listItem: "10% improvement in first-time customer conversion rates, due to structured communication." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation
                                subTitle={"Moving from chaos to clarity in customer engagement"}
                                descriptions={[
                                    "Sedar Global's adoption of Gallabox has transformed its customer engagement process from a manual, fragmented workflow into a structured, efficient, and technology-driven system. By replacing WhatsApp-only tracking and Excel-based follow-ups with Gallabox's shared inbox, chatbots, broadcasts, and integrations, the organization has significantly improved team productivity, customer engagement, and conversion rates.",
                                    "Even at an early stage of adoption, Gallabox has proven to be a cost-effective, supportive, and convenient solution that empowers Sedar Global to streamline operations and build stronger relationships with its customers.",
                                ]}
                                className="mt-10"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto">
                <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-20">
                    {casestudyCards.slice(0, 3)?.map((data, index) => (
                        <CasestudyCard key={index} {...data} />
                    ))}
                </div>
            </section>
        </NewLayout>
    );
};

export default SedarGlobal;
